import useNavigateWithScrollSave, { checkIsMenuExpanded } from 'hooks/useNavigateWithScrollSave'
import { selectWarehouse } from '../Warehouses/warehousesSlice'
import useWarehousesLoading from 'hooks/useWarehousesLoading'
import React, { useContext, useEffect, useState, useRef } from 'react'
import { ViewContext } from 'context/ViewContext'
import KeyCabinetBadge from 'containers/KeyCabinet/Badge'
import WarehouseSlides from './WarehouseSlides'
import { useParams } from 'react-router-dom'
import MagNumber from 'components/MagNumber'
import MagPromotion from './MagPromotion'
import { useReduxSelector } from 'store'
import MagActions from './MagActions'
import MagHistory from './MagHistory'
import MagAccess from './MagAccess'
import MagStatus from './MagStatus'
import MagManage from './MagManage'
import Alarm from './Alarm'
import './style.scss'

const statuses = <MagStatus />
const promotion = <MagPromotion />

export const SECTION_TYPES = {
  access: 'access',
  cancel: 'cancel',
  details: 'details',
  history: 'history',
  invoices: 'invoices',
  extension: 'extension',
  accessories: 'accessories',
  termination: 'termination'
}

const type = process.env.REACT_APP_TYPE

const Warehouse: React.FC = () => {
  const mobile = useContext(ViewContext)

  const { id } = useParams<warehouse.PageParams>()
  const { number, contractId, isShared, acronym, permissions, sharing, events, hasAlarmZone, hasAccess, rentableType, actions } = useReduxSelector(state => selectWarehouse(state, id))
  const hasBadge = hasAccess && actions?.key_cabinet?.grant?.canOpen
  const [expanded, expand] = useState(checkIsMenuExpanded())
  useWarehousesLoading(!contractId, true)

  const menuRef = useRef<HTMLDivElement>(null)
  const menuPanelRef = useRef<HTMLDivElement>(null)
  const warehouseRef = useRef<HTMLDivElement>(null)

  const scrolledUp = useRef(true)
  const navigate = useNavigateWithScrollSave(expanded, menuPanelRef)

  useEffect(() => {
    if (mobile) {
      document.body.style.setProperty('overflow', 'hidden')
      return () => {
        document.body.style.removeProperty('overflow')
      }
    }
  }, [mobile])

  const onTouch = (e: React.TouchEvent<HTMLDivElement>) => {
    const menu = menuRef.current
    if (!menu || !mobile) return

    const initialY = e.touches[0].clientY
    const touchMove = (e: TouchEvent) => {
      const currentY = e.touches[0].clientY
      if ((initialY - currentY > 40 && !expanded) || (currentY - initialY > 40 && expanded)) {
        expand(!expanded)
        touchEnd()
      }
    }

    const touchEnd = () => {
      menu.removeEventListener('touchend', touchEnd, false)
      menu.removeEventListener('touchmove', touchMove, false)
    }

    menu.addEventListener('touchend', touchEnd, false)
    menu.addEventListener('touchmove', touchMove, false)
  }

  const scrollPage = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const { scrollTop } = e.target as HTMLDivElement
    if (!expanded && scrollTop > 160) expand(true)
    else if (expanded && scrollTop < 20) expand(false)
  }

  const scrollMenu = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const { scrollTop } = e.target as HTMLDivElement
    scrolledUp.current = scrollTop < 1
  }

  const scroll = (ref: React.RefObject<HTMLElement>, behavior: ScrollBehavior = 'smooth') => {
    ref.current?.scrollIntoView({ behavior })
  }

  const toggleMenu = () => {
    if (menuRef.current && expanded) menuRef.current.scrollTop = 0
    mobile ? expand(!expanded) : scroll(expanded ? warehouseRef : menuRef)
  }

  return (
    <div
      className='warehouse'
      onScroll={e => !mobile && scrollPage(e)}
      style={{ overflow: mobile ? 'hidden' : 'auto' }}>
      <div
        ref={warehouseRef}
        className='warehouse__main center'>
        <MagNumber
          number={number}
          acronym={acronym}
          isShared={isShared}
          rentableType={rentableType}
        />
        {hasBadge && <KeyCabinetBadge
          id={id}
          fromWarehouse
          purpose={actions?.key_cabinet?.grant?.purpose}
        />}
        <WarehouseSlides id={id} />
      </div>

      <div
        ref={menuRef}
        className='menu__main'
        style={(expanded && mobile) ? { top: ((isShared ? 15 : 0) + ((type === 'mm' && rentableType === 'parking') ? 15 : 0)) } : undefined}
        onTouchStart={e => (mobile && scrolledUp.current) && onTouch(e)}>

        <div
          className='menu__full'
          onTouchStart={e => mobile && onTouch(e)}>
          {statuses}
        </div>

        <div
          ref={menuPanelRef}
          onScroll={e => mobile && scrollMenu(e)}
          style={{ overflowY: expanded ? 'auto' : 'hidden' }}>

          <MagActions
            id={id}
            expanded={expanded}
            navigate={navigate}
            toggleMenu={toggleMenu} />

          {(expanded || !mobile) && <>
            <MagManage
              id={id}
              navigate={navigate}
            />
            {hasAccess && hasAlarmZone && <Alarm />}
            {(Array.isArray(events) && events.length > 0 && (!isShared || permissions?.includes('events'))) && <MagHistory
              id={id}
              more={() => {
                navigate(`/warehouses/${id}/${SECTION_TYPES.history}`)
              }}
            />}
            {Array.isArray(sharing) && sharing.length > 0 && (!isShared || permissions?.includes('share')) && <MagAccess more={() => {
              navigate(`/warehouses/${id}/${SECTION_TYPES.access}`)
            }} />}
            {promotion}
          </>}
        </div>
      </div>
    </div>
  )
}

export default Warehouse